<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import MaskInput from 'svelte-input-mask/MaskInput.svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'

    import type { IAddress } from 'wdom/services/contacts'

    import type { User, UserHandler, UserNotification, UserNotificationDeliveryArgsEmail, UserNotificationDeliveryArgsSms } from './user'

    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'

    const userh = getContext('User') as UserHandler

    // const status = sderived(userh.status, ($status) => $status)
    // const loading = sderived(userh.loading, ($loading) => $loading)

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const notifications: Readable<UserNotification[]> = sderived(userh.notifications, ($v) => $v)

    const selectedAddress = getContext('selectedAddress') as Writable<IAddress>

    type NotifAction = null | 'sms' | 'email' | UserNotification
    let notifAction: NotifAction = $state(null)
    let notifToRemove: null | UserNotification = $derived(notifAction != null && notifAction !== 'sms' && notifAction !== 'email' ? notifAction : null)
    let working = $state(false)

    // const configuredEmails = new Set($user.notifications.filter((v) => v.delivery_mode === 'email').map((v) => (v.delivery as UserNotificationDeliveryEmail).email))
    // const configuredPhones = new Set($user.notifications.filter((v) => v.delivery_mode === 'sms').map((v) => (v.delivery as UserNotificationDeliverySms).phone))

    interface NotifEdit {
        days_delta: number
        delivery_mode: 'sms' | 'email'
        delivery_phone?: string
        delivery_email?: string
    }

    const schema = yup.object().shape({
        days_delta: yup.number().min(1).max(90).required(),
        delivery_mode: yup
            .string()
            .matches(/^sms|email$/)
            .required(),
        delivery_phone: yup.string().when('delivery_mode', ([mode], lschema: yup.StringSchema) => {
            if (mode == 'sms')
                return (
                    lschema
                        .matches(/^(\+1\.\d{10}(x\d+)?)?$/)
                        // .notOneOf([...configuredPhones])
                        .required()
                )
            return lschema.optional()
        }),
        delivery_email: yup.string().when('delivery_mode', ([mode], lschema: yup.StringSchema) => {
            if (mode == 'email')
                return (
                    lschema
                        .email()
                        // .notOneOf([...configuredEmails])
                        .required()
                )
            return lschema.optional()
        }),
    })

    let orig: NotifEdit = {
        // @ts-expect-error
        days_delta: null,
        // @ts-expect-error
        delivery_mode: null,
        delivery_phone: '',
        delivery_email: '',
    }
    let form: NotifEdit = $state({ ...orig })

    // Special, for MaskInput.. we reset this at the same time we reset 'form'
    let phoneNumberEdit = $state('')

    function setDefaults(mode: 'sms' | 'email') {
        form.delivery_mode = mode
        if (form.delivery_mode) {
            if (form.delivery_mode == 'sms') {
                form.delivery_email = ''
                form.delivery_phone = $selectedAddress.mobile && $selectedAddress.mobile.startsWith('+1.') ? $selectedAddress.mobile : ''
                phoneNumberEdit = form.delivery_phone.startsWith('+1.') ? form.delivery_phone.slice(3) : ''
            } else {
                form.delivery_email = $selectedAddress.email ? $selectedAddress.email : ''
                form.delivery_phone = ''
            }
        }
        notifAction = mode
    }

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    let formIsValid = $derived(schema.isValidSync(form))
    let days_delta_isValid = $derived(validateSyncAt('days_delta', form))
    let delivery_phone_isValid = $derived(validateSyncAt('delivery_phone', form))
    let delivery_email_isValid = $derived(validateSyncAt('delivery_email', form))

    interface SaveNotification {
        days_delta: number
        delivery_mode: 'sms' | 'email'
        delivery_args: UserNotificationDeliveryArgsSms | UserNotificationDeliveryArgsEmail
    }

    async function remove() {
        working = true
        const notif = notifAction as UserNotification
        try {
            const r = await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/notification/${notif.id}`)
            notify({
                type: 'success',
                title: $_('Notification removal'),
                text: $_('The notification was successfully removed.'),
            })
            form = { ...orig }
            userh.raw.notifications = r.data.notifications
            userh.loadNotificationData()
            notifAction = null
        } catch (e) {
            console.error('Failed to remove notification:', e)
            notify({
                type: 'danger',
                title: $_('Notification removal'),
                text: $_('The notification could not be removed.'),
            })
        }
        working = false
    }

    async function save() {
        let data: SaveNotification
        if (form.delivery_mode == 'sms') {
            data = {
                days_delta: form.days_delta,
                delivery_mode: 'sms',
                delivery_args: {
                    // @ts-expect-error
                    phone: form.delivery_phone,
                },
            }
        } else {
            data = {
                days_delta: form.days_delta,
                delivery_mode: 'email',
                delivery_args: {
                    // @ts-expect-error
                    email: form.delivery_email,
                },
            }
        }

        working = true
        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/${$user.id}/notification`, data)
            notify({
                type: 'success',
                title: $_('Add a notification'),
                text: $_('The notification was saved.'),
            })
            form = { ...orig }
            userh.raw.notifications = r.data.notifications
            userh.loadNotificationData()
            notifAction = null
        } catch (e) {
            console.error('Failed to add notification:', e)
            notify({
                type: 'danger',
                title: $_('Add a notification'),
                text: $_('The new notification could not be saved.'),
            })
        }
        working = false
    }

    // Modal handling stuff

    $effect(() => {
        if ((notifAction === 'sms' || notifAction === 'email') && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (notifAction !== 'sms' && notifAction !== 'email' && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    onDestroy(() => {
        if ((notifAction === 'sms' || notifAction === 'email') && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })

    function delivery_args_email(args: any) {
        return args as UserNotificationDeliveryArgsEmail
    }
    function delivery_args_sms(args: any) {
        return args as UserNotificationDeliveryArgsSms
    }

    interface MaskInputChangeEvent {
        element: HTMLInputElement
        inputState: { unmaskedValue: string; maskedValue: string; visibleValue: string }
    }
    function inputOnchange(detail: MaskInputChangeEvent) {
        form.delivery_phone = `+1.${detail.inputState.unmaskedValue}`
        phoneNumberEdit = detail.inputState.unmaskedValue
    }
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape' && (notifAction === 'sms' || notifAction === 'email')) notifAction = null
    }}
/>

<dt>{$_('Notifications')}</dt>
<dd>
    {#if !$notifications || !$notifications.length}
        <p>{$_('No notifications')}</p>
    {:else}
        <ul>
            {#each $notifications as notif (notif.id)}
                <li>
                    <span style="display: flex">
                        <span style="flex: 1 0 0">
                            {$ngettext('{n} day', '{n} days', { n: notif.days_delta })}: {notif.delivery_mode} /

                            {#if notif.delivery_mode == 'email'}{delivery_args_email(notif.delivery_args).email}{:else}<InternationalPhone phone={delivery_args_sms(notif.delivery_args).phone} />{/if}
                        </span>
                        <span style="flex: 0 0 auto">
                            {#if notifToRemove && notifToRemove.id == notif.id}
                                <button type="button" onclick={() => remove()} class="btn btn-sm btn-danger" style="margin-right: 0.25em"><Icon inline icon="fa:trash" /></button>
                                <button type="button" onclick={() => (notifAction = null)} class="btn btn-sm btn-success"><Icon inline icon="fa:ban" /></button>
                            {:else if !working}
                                <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => (notifAction = notif)}><Icon inline icon="fa:trash" /></button>
                            {/if}
                        </span>
                    </span>
                </li>
            {/each}
        </ul>
    {/if}
    {#if working}
        <div>
            <button class="btn btn-link btn-a" type="button"
                ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                Un moment…</button
            >
        </div>
    {:else}
        <p>
            <button class="btn btn-link btn-a" type="button" onclick={() => setDefaults('sms')}>{$_('Add SMS')}</button>
            <span class="text-muted">•</span>
            <button class="btn btn-link btn-a" type="button" onclick={() => setDefaults('email')}>{$_('Add email')}</button>
        </p>
    {/if}
</dd>

{#if notifAction == 'sms' || notifAction == 'email'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <form
                    class="form-horizontal"
                    onsubmit={(e) => {
                        e.preventDefault()
                        save()
                    }}
                    novalidate
                >
                    <div class="modal-header text-bg-primary">
                        <div>{notifAction == 'sms' ? $_('Add SMS notification') : $_('Add email notification')}</div>
                    </div>
                    <div class="modal-body">
                        <div class="form-horizontal">
                            {#if notifAction == 'sms'}
                                <div class="form-group">
                                    <label for="sms" class="col-sm-4 col-form-label">{$_('Numéro de téléphone')}</label>
                                    <div class="col-sm-8">
                                        <MaskInput class="form-control {delivery_phone_isValid ? '' : 'is-invalid'}" alwaysShowMask={false} placeholder={$lg == 'fr' ? '000 000-0000' : '000-000-0000'} mask={$lg == 'fr' ? '000 000-0000' : '000-000-0000'} disabled={working} value={phoneNumberEdit} on:change={(e) => inputOnchange(e.detail)} />
                                    </div>
                                </div>
                            {:else}
                                <div class="form-group">
                                    <label for="email" class="col-sm-4 col-form-label">{$_('Adresse courriel')}</label>
                                    <div class="col-sm-8">
                                        <input type="email" class="form-control" id="email" bind:value={form.delivery_email} disabled={working} class:is-invalid={!delivery_email_isValid} />
                                    </div>
                                </div>
                            {/if}
                            <div class="form-group">
                                <label for="ndays" class="col-sm-4 col-form-label">{$_('Jours (1…90)')}</label>
                                <div class="col-sm-8">
                                    <input type="number" class="form-control" id="ndays" bind:value={form.days_delta} disabled={working} class:is-invalid={!days_delta_isValid} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (notifAction = null)}>{$_('Cancel')}</button>
                        <button type="submit" class="btn btn-primary" disabled={working || !formIsValid}>{$_('Save')}</button>
                    </div>
                </form>
            </div>
        </div>
        <div
            class="modal-backdrop"
            aria-hidden="true"
            onclick={() => {
                notifAction = null
            }}
        ></div>
    </div>
{/if}
